/**
 * InBooksDetailModal (iter1404 Phase 4b)
 *
 * "In Books" drill: for any posted bank-feed transaction, shows the
 * composite journal (GL codes + DR/CR lines), every linked document
 * (with deep links to their detail pages), the settlement id, and a
 * jump to the journal entry itself.
 *
 * Backend: GET /api/bank-feeds/in-books/{transaction_id}
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Loader2, AlertCircle, ExternalLink, BookOpen, Copy, CheckCircle2,
  ArrowRight, FileText, Hash,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../../utils/currency';
import { RowFxEquivalent } from './RowFxEquivalent';

const API = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPE_LABEL = {
  invoice: 'Invoice',
  bill: 'Bill',
  loan: 'Loan',
  fixed_asset: 'Fixed Asset',
  investment: 'Investment',
  prepayment: 'Prepayment',
  deferred_revenue: 'Deferred Revenue',
  statutory_payable: 'Statutory',
  payroll_run: 'Payroll',
  manual_journal: 'Manual Journal',
  other_receipt: 'Other Receipt',
  other_payment: 'Other Payment',
};

const AT_COLOR = {
  asset: 'text-emerald-700',
  liability: 'text-orange-700',
  equity: 'text-purple-700',
  revenue: 'text-blue-700',
  expense: 'text-rose-700',
};

export default function InBooksDetailModal({
  open, onOpenChange, transaction, bankCurrency, baseCurrency,
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const tx = transaction || {};

  const fetchData = useCallback(async () => {
    if (!tx?.id) return;
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(
        `${API}/api/bank-feeds/in-books/${tx.id}`,
        { withCredentials: true },
      );
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load journal details');
    } finally {
      setLoading(false);
    }
  }, [tx?.id]);

  useEffect(() => {
    if (open) fetchData();
    if (!open) setData(null);
  }, [open, fetchData]);

  const copyId = (id) => {
    navigator.clipboard?.writeText(id).then(
      () => toast.success('Copied to clipboard'),
      () => toast.error('Copy failed'),
    );
  };

  const goto = (path) => {
    if (!path) return;
    onOpenChange(false);
    navigate(path);
  };

  const journal = data?.journal;
  const settlement = data?.settlement;
  const linked = data?.linked_docs || [];
  const gls = data?.gl_codes_hit || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col p-0"
        data-testid="in-books-modal">
        <DialogHeader className="px-6 pt-5 pb-3 border-b bg-gradient-to-b from-emerald-50/60 to-transparent">
          <DialogTitle className="flex items-center gap-2 text-base">
            <BookOpen className="w-4 h-4 text-emerald-600" />
            In Books · Transaction detail
          </DialogTitle>
          <DialogDescription className="text-xs mt-1">
            {tx.direction === 'inflow' ? 'Money received' : 'Money paid'}
            {' · '}
            <span className="font-mono">
              {formatCurrency(tx.amount, tx.currency || tx.bank_currency || bankCurrency || 'USD')}
            </span>
            {' '}
            <RowFxEquivalent tx={tx} rowCurrency={tx.currency || tx.bank_currency || bankCurrency} baseCurrency={baseCurrency} />
            {' · '}
            <span className="italic">
              {(tx.description || '').slice(0, 80)}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {loading && (
            <div className="flex items-center justify-center py-20 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Loading journal details…
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-700 p-3 bg-red-50 border border-red-200 rounded">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          )}
          {!loading && !error && !journal && (
            <div className="text-center py-16 text-slate-500 text-sm">
              No journal was posted for this transaction yet.
            </div>
          )}
          {!loading && journal && (
            <>
              {/* Journal summary */}
              <section data-testid="ib-journal-section"
                       className="rounded-lg border border-slate-200 overflow-hidden">
                <header className="px-4 py-2.5 bg-slate-50 border-b flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">
                      Journal
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Hash className="w-3.5 h-3.5 text-slate-400" />
                      <code className="text-xs font-mono text-slate-800 truncate"
                            data-testid="ib-journal-id">
                        {journal.id}
                      </code>
                      <button
                        onClick={() => copyId(journal.id)}
                        className="text-slate-400 hover:text-slate-700 transition"
                        title="Copy journal id"
                        data-testid="ib-copy-journal-id">
                        <Copy className="w-3 h-3" />
                      </button>
                      {journal.is_reversed && (
                        <Badge className="text-[10px] bg-purple-100 text-purple-700 border-purple-200">
                          Reversed
                        </Badge>
                      )}
                      <Badge
                        className={`text-[10px] ${
                          journal.totals?.balanced
                            ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                            : 'bg-red-100 text-red-700 border-red-200'
                        }`}>
                        {journal.totals?.balanced ? (
                          <><CheckCircle2 className="w-2.5 h-2.5 mr-1 inline" /> Balanced</>
                        ) : 'Unbalanced'}
                      </Badge>
                    </div>
                    {journal.reference && (
                      <div className="text-[11px] text-slate-500 mt-1 truncate">
                        {journal.reference} · {journal.date}
                      </div>
                    )}
                  </div>
                  <Button
                    size="sm" variant="outline"
                    className="h-7 text-xs shrink-0"
                    onClick={() => goto(journal.deep_link)}
                    data-testid="ib-goto-journal">
                    Open journal <ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                </header>
                <div className="overflow-x-auto">
                  {/* iter1416 · Cross-currency banner — only shown
                      when there is real FX to disclose (settlement
                      exchange_rate, or at least one line stamped
                      with a native_currency that differs from the
                      functional currency). Same-currency journals
                      don't need this noise. */}
                  {(() => {
                    const _fc = (journal.functional_currency
                                 || tx.currency || '').toUpperCase();
                    const _hasFxRate = journal.exchange_rate
                      && Number(journal.exchange_rate) > 0
                      && Math.abs(Number(journal.exchange_rate) - 1) > 1e-9;
                    const _hasCrossLine = (journal.lines || []).some(
                      (l) => l.native_currency
                             && String(l.native_currency).toUpperCase() !== _fc);
                    return (_hasFxRate || _hasCrossLine);
                  })() && (
                    <div className="px-4 py-2 bg-purple-50/50 border-b border-purple-100 text-[11px] text-purple-900 flex flex-wrap items-center gap-x-3 gap-y-1"
                         data-testid="ib-fx-context">
                      {journal.functional_currency && (
                        <span>
                          Functional currency:{' '}
                          <span className="font-mono font-medium">
                            {journal.functional_currency}
                          </span>
                        </span>
                      )}
                      {journal.exchange_rate && (
                        <span>
                          Settlement rate:{' '}
                          <span className="font-mono font-medium">
                            {Number(journal.exchange_rate).toLocaleString(
                              undefined, {maximumFractionDigits: 6})}
                          </span>
                        </span>
                      )}
                      <span className="text-purple-700/80">
                        Amounts below are the JOURNAL (functional-currency) view;
                        native values shown in muted text where different.
                      </span>
                    </div>
                  )}
                  <table className="w-full text-xs">
                    <thead className="text-slate-500 bg-slate-50/50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">GL</th>
                        <th className="text-left px-3 py-2 font-medium">Account</th>
                        <th className="text-right px-3 py-2 font-medium">Debit</th>
                        <th className="text-right px-3 py-2 font-medium">Credit</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {(journal.lines || []).map((ln, i) => {
                        // iter1415 · Cross-currency lines carry the
                        // native amount + native currency + per-line
                        // FX rate. Render native primary + functional
                        // secondary so users see BOTH values.
                        const _funcCcy = journal.functional_currency
                          || tx.currency || 'USD';
                        const _nativeCcy = ln.native_currency;
                        const _crossCcy = _nativeCcy
                          && _nativeCcy !== _funcCcy;
                        const _nativeDr = Number(ln.native_debit || 0);
                        const _nativeCr = Number(ln.native_credit || 0);
                        return (
                        <tr key={i} className="hover:bg-slate-50/70"
                            data-testid={`ib-journal-line-${i}`}>
                          <td className="px-3 py-2 font-mono">
                            <button
                              onClick={() => goto(ln.coa_deep_link)}
                              className="text-blue-600 hover:underline"
                              title="Open in CoA"
                              data-testid={`ib-gl-link-${ln.account_code}`}>
                              {ln.account_code}
                            </button>
                          </td>
                          <td className="px-3 py-2">
                            <div className={AT_COLOR[ln.account_type] || 'text-slate-700'}>
                              {ln.account_name || '—'}
                              {ln.tax_state && (
                                <Badge className="ml-2 text-[9px] bg-slate-100 text-slate-600 border-slate-200">
                                  {ln.tax_state}
                                </Badge>
                              )}
                              {_crossCcy && (
                                <Badge
                                  className="ml-2 text-[9px] bg-purple-50 text-purple-700 border-purple-200"
                                  title={`Native ${_nativeCcy} @ rate ${ln.line_fx_rate || 1}`}>
                                  {_nativeCcy} @ {Number(ln.line_fx_rate || 1)
                                    .toLocaleString(undefined,{maximumFractionDigits: 4})}
                                </Badge>
                              )}
                            </div>
                            <div className="text-[10px] text-slate-500 truncate">
                              {ln.description}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right font-mono">
                            {ln.debit > 0 ? (
                              <>
                                <div>{formatCurrency(ln.debit, _funcCcy)}</div>
                                {_crossCcy && _nativeDr > 0 && (
                                  <div className="text-[10px] text-slate-400"
                                       data-testid={`ib-native-debit-${i}`}>
                                    {formatCurrency(_nativeDr, _nativeCcy)}
                                  </div>
                                )}
                              </>
                            ) : ''}
                          </td>
                          <td className="px-3 py-2 text-right font-mono">
                            {ln.credit > 0 ? (
                              <>
                                <div>{formatCurrency(ln.credit, _funcCcy)}</div>
                                {_crossCcy && _nativeCr > 0 && (
                                  <div className="text-[10px] text-slate-400"
                                       data-testid={`ib-native-credit-${i}`}>
                                    {formatCurrency(_nativeCr, _nativeCcy)}
                                  </div>
                                )}
                              </>
                            ) : ''}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-slate-50 border-t border-slate-200">
                      <tr className="font-medium">
                        <td colSpan={2} className="px-3 py-2 text-right text-slate-500 uppercase text-[10px] tracking-wide">
                          Totals
                        </td>
                        <td className="px-3 py-2 text-right font-mono"
                            data-testid="ib-total-debit">
                          {formatCurrency(journal.totals?.debit || 0,
                            journal.functional_currency || tx.currency || 'USD')}
                        </td>
                        <td className="px-3 py-2 text-right font-mono"
                            data-testid="ib-total-credit">
                          {formatCurrency(journal.totals?.credit || 0,
                            journal.functional_currency || tx.currency || 'USD')}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </section>

              {/* GL codes hit summary */}
              {gls.length > 0 && (
                <section data-testid="ib-gl-codes">
                  <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">
                    GL codes hit
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {gls.map(code => (
                      <button
                        key={code}
                        onClick={() => goto(`/chart-of-accounts?code=${code}`)}
                        className="text-[11px] font-mono px-2.5 py-1 rounded-full border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition"
                        data-testid={`ib-gl-chip-${code}`}>
                        {code}
                      </button>
                    ))}
                  </div>
                </section>
              )}

              {/* Linked docs */}
              <section data-testid="ib-linked-docs">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">
                  Linked documents ({linked.length})
                </div>
                {linked.length === 0 && (
                  <div className="text-xs text-slate-500 italic">
                    No linked documents recorded for this transaction.
                  </div>
                )}
                <div className="grid gap-2">
                  {linked.map((doc, i) => (
                    <div
                      key={`${doc.doc_type}-${doc.document_id}-${i}`}
                      className="p-3 rounded-lg border border-slate-200 hover:border-blue-300 bg-white flex items-center justify-between gap-3"
                      data-testid={`ib-doc-${doc.document_id}`}>
                      <div className="flex items-start gap-3 min-w-0">
                        <FileText className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium truncate">
                              {doc.doc_number || doc.document_id.slice(0, 8)}
                            </span>
                            <Badge className="text-[10px] bg-slate-100 text-slate-700 border-slate-200">
                              {DOC_TYPE_LABEL[doc.doc_type] || doc.doc_type}
                            </Badge>
                            {doc.scenario && (
                              <Badge className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                                {doc.scenario.replace(/_/g, ' ')}
                              </Badge>
                            )}
                            {doc.current_status && (
                              <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                                {doc.current_status}
                              </Badge>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                            {doc.counterparty || '—'}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            Applied{' '}
                            <span className="font-mono text-slate-700">
                              {formatCurrency(doc.applied_amount, doc.currency || tx.currency || 'USD')}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm" variant="ghost"
                        className="h-7 text-xs shrink-0"
                        onClick={() => goto(doc.deep_link)}
                        disabled={!doc.deep_link}
                        data-testid={`ib-doc-open-${doc.document_id}`}>
                        Open <ArrowRight className="w-3 h-3 ml-1" />
                      </Button>
                    </div>
                  ))}
                </div>
              </section>

              {/* Settlement id */}
              {settlement && (
                <section
                  className="text-[11px] text-slate-500 flex items-center gap-2"
                  data-testid="ib-settlement">
                  <span>Settlement ref:</span>
                  <code className="font-mono text-slate-700">{settlement.id}</code>
                  <button
                    onClick={() => copyId(settlement.id)}
                    className="text-slate-400 hover:text-slate-700 transition"
                    title="Copy settlement id">
                    <Copy className="w-3 h-3" />
                  </button>
                  {settlement.status === 'reversed' && (
                    <Badge className="text-[9px] bg-purple-100 text-purple-700 border-purple-200">
                      Reversed
                    </Badge>
                  )}
                </section>
              )}
            </>
          )}
        </div>

        <DialogFooter className="px-6 py-3 border-t">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}
            data-testid="ib-close">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
